// src/components/Billing/StripeInput.js
// @flow

import React, {
  forwardRef,
  useRef,
  useImperativeHandle
} from 'react';
import * as R from 'ramda';
import { log } from '../../utils/jsUtils';
import { useTheme } from '@mui/material/styles';

// Replaces "withStyles" usage:
import stripeInputStyles from './Styles/StripeInput.Style';
import combineStyles, { createClasses } from '../../utils/stylesUtils';

/**
 * The same "base" style logic you had:
 */
const fixedStyle = {
  color: '#202020',
  fontFamily: 'Product Sans, Open Sans, sans-serif',
  fontSmoothing: 'antialiased',
  '&::placeholder': {
    color: '#202020'
  }
};

type Props = {
  // your old style object, no longer needed if not used, but we keep it for backward comp
  component: Function,
  inputRef: Function | { current: any }
  // plus any old props you had
};

const StripeInput = forwardRef((props: Props, ref) => {
  const {
    component: Component,
    inputRef,
    ...other
  } = props;

  const theme = useTheme();
  const classes = createClasses(stripeInputStyles, theme); // classes remain available if needed

  const elementRef = useRef(null);

  // This is the key part for MUI's inputComponent protocol
  useImperativeHandle(ref, () => ({
    // To work properly with MUI, focus and other input methods need to be defined here
    focus: () => elementRef.current && elementRef.current.focus(),
    // We also need to expose the complete element for MUI's validation
    node: elementRef.current,
    // And any other methods that would be expected on a standard input
    value: ''
  }));

  // Also properly handle the inputRef if provided
  useImperativeHandle(inputRef, () => ({
    focus: () => elementRef.current && elementRef.current.focus(),
    node: elementRef.current,
    value: ''
  }));

  // log('[StripeInput] other props: ', other);

  // We pick only recognized props to pass on:
  const pickedOther = R.pick(
    [
      'className',
      'disabled',
      'id',
      'onBlur',
      'onFocus',
      'onChange',
      'placeholder',
      'type',
      'value'
    ],
    other
  );

  // log('[StripeInput] pickedOther: ', pickedOther);

  return (
    <Component
      onReady={element => {
        elementRef.current = element;
      }}
      // your old "style={{ base: fixedStyle }}"
      style={{ base: fixedStyle }}
      {...pickedOther}
    />
  );
});

StripeInput.displayName = 'StripeInput';

export default (StripeInput: React.ComponentType<Props>);